/**
 * Human support via WhatsApp or email (ELARA-750).
 *
 * Elara delivers human concierge support through WhatsApp rather than an in-app
 * thread, with email as the alternative for travelers who don't use WhatsApp (or
 * would simply rather write). Both web and mobile deep-link out — to the business
 * number or the support inbox — with a prefilled, editable message carrying light
 * identity/trip context, so the concierge team knows who is writing and about which
 * trip. This replaces the former web-only Tidio widget, which had no React Native
 * build.
 *
 * Both channels prefill the same body ({@link buildSupportPrefillMessage}), so the
 * context the concierge receives doesn't depend on which one the traveler picks.
 *
 * Phase 1 is deep-link only (no WhatsApp Business API access on the current
 * plan). A later phase may sync transcripts back into Firestore; keeping the
 * number, address, and message construction here means callers never hardcode any.
 */

/**
 * Elara's WhatsApp support number in E.164 form **without** the leading `+`, as
 * required by `wa.me` click-to-chat links. Single source of truth for web and
 * mobile. Currently: +1 (267) 936-4041.
 */
export const SUPPORT_WHATSAPP_NUMBER = '12679364041';

/**
 * Elara's support inbox, for travelers who'd rather write than message. Lives here beside the
 * WhatsApp number so no call site hardcodes a way to reach the concierge team.
 */
export const SUPPORT_EMAIL = 'support@elara.travel';

/**
 * Base URL of the deployed web app, for linking the trip itself (`/trip/:tripId`) into the
 * traveler's own prefilled message — the traveler composes and can edit this message before it
 * ever sends, so it must only ever carry the same user-facing trip link they could get to
 * themselves, never a staff/admin URL.
 */
const APP_BASE_URL = 'https://app.elara.travel';

export interface SupportChatContext {
  /** Traveler's display name, for a greeting the concierge can recognise. */
  name?: string | null;
  /** Human-readable title/destination of the trip they're asking about. */
  tripName?: string | null;
  /** Trip id, to link the trip itself so the concierge can find it faster than by account email. */
  tripId?: string | null;
}

/**
 * Builds the human-readable, prefilled message body. The traveler can edit or
 * clear it in WhatsApp before sending — it is a convenience for the agent, not
 * a contract.
 *
 * @example
 * buildSupportPrefillMessage({ name: 'Jane', tripName: 'Kyoto', tripId: 'abc123' });
 * // "Hi Shannon,\nThis is Jane. I'd like some help with my Kyoto trip.\nhttps://app.elara.travel/trip/abc123\n\n"
 */
export function buildSupportPrefillMessage({ name, tripName, tripId }: SupportChatContext = {}): string {
  // The salutation gets its own line (it reads as a written note rather than a run-on sentence), so
  // the ask is joined with a newline — not a space, which would indent the second line.
  const salutation = name ? 'Hi Shannon,' : 'Hi Elara team,';
  const intro = name ? `This is ${name}. ` : '';
  const ask = tripName
    ? `I'd like some help with my ${tripName} trip.`
    : "I'd like some help with my travel plans.";
  const tripLink = tripId ? `${APP_BASE_URL}/trip/${tripId}` : '';
  return `${salutation}\n${intro}${ask}\n${tripLink}\n\n`;
}

/**
 * Builds a WhatsApp click-to-chat URL to Elara support, with the prefilled
 * message from {@link buildSupportPrefillMessage}. Open it with the platform's
 * URL opener (`window.open` on web, `Linking.openURL` on native).
 */
export function buildSupportWhatsappUrl(
  context: SupportChatContext = {},
  whatsappNumber: string = SUPPORT_WHATSAPP_NUMBER,
): string {
  const text = encodeURIComponent(buildSupportPrefillMessage(context));
  return `https://wa.me/${whatsappNumber}?text=${text}`;
}

/**
 * Builds the subject line for a support email — trip-aware, so the concierge team can triage the
 * inbox without opening the message.
 *
 * @example
 * buildSupportEmailSubject({ tripName: 'Kyoto' }); // "Help with my Kyoto trip"
 */
export function buildSupportEmailSubject({ tripName }: SupportChatContext = {}): string {
  return tripName ? `Help with my ${tripName} trip` : 'Help with my travel plans';
}

/**
 * Builds a `mailto:` URL to Elara support, prefilling the same body WhatsApp gets
 * ({@link buildSupportPrefillMessage}) plus a trip-aware subject. Open it with the platform's URL
 * opener (`window.open` on web, `Linking.openURL` on native), which hands off to whichever mail
 * client the traveler has — nothing is sent until they send it.
 */
export function buildSupportEmailUrl(
  context: SupportChatContext = {},
  email: string = SUPPORT_EMAIL,
): string {
  const subject = encodeURIComponent(buildSupportEmailSubject(context));
  const body = encodeURIComponent(buildSupportPrefillMessage(context));
  return `mailto:${email}?subject=${subject}&body=${body}`;
}
